import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import { Textarea } from "@arcadeai/design-system/components/ui/atoms/textarea";
import type { ControllerRenderProps } from "react-hook-form";
import type { Parameter } from "@/app/tools/types";

type TypeSpecificInputProps = {
  parameter: Parameter;
  field: ControllerRenderProps<Record<string, unknown>, string>;
  autoFocus?: boolean;
  onChange?: (value: string | undefined) => void;
};

export function TypeSpecificInput({
  parameter,
  field,
  autoFocus,
  onChange,
}: TypeSpecificInputProps) {
  const valueSchema = parameter.value_schema;
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const stringValue = field.value as string | undefined;

  // Handle enum type (dropdown)
  if (valueSchema.enum) {
    return (
      <Select
        onValueChange={(next) => {
          if (next == null) {
            return;
          }
          onChange?.(next);
        }}
        value={stringValue ?? ""}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {valueSchema.enum.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Handle different value types
  switch (valueSchema.val_type) {
    case "boolean":
      return (
        <div className="flex items-center gap-2">
          <Switch
            checked={stringValue === "true"}
            onCheckedChange={(checked) => onChange?.(checked.toString())}
          />
          <p className="text-muted-foreground text-sm">
            {stringValue === "true" ? "True" : "False"}
          </p>
        </div>
      );

    case "array":
      return (
        <Textarea
          autoFocus={autoFocus}
          name={field.name}
          onBlur={field.onBlur}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Enter comma-separated values (e.g., alpha, beta, gamma)"
          ref={field.ref}
          value={stringValue ?? ""}
        />
      );

    case "json":
      return (
        <Textarea
          autoFocus={autoFocus}
          className="font-mono"
          name={field.name}
          onBlur={field.onBlur}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder='{"key":"value"}'
          ref={field.ref}
          rows={4}
          value={stringValue ?? ""}
        />
      );

    case "integer":
    case "number":
      return (
        <Input
          autoFocus={autoFocus}
          name={field.name}
          onBlur={field.onBlur}
          onChange={(e) => onChange?.(e.target.value)}
          ref={field.ref}
          step={valueSchema.val_type === "integer" ? "1" : "any"}
          type="number"
          value={stringValue ?? ""}
        />
      );

    default:
      return (
        <Input
          autoFocus={autoFocus}
          name={field.name}
          onBlur={field.onBlur}
          onChange={(e) => onChange?.(e.target.value)}
          ref={field.ref}
          value={stringValue ?? ""}
        />
      );
  }
}
